import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes, FaChevronDown } from "react-icons/fa";
import Images from "../../utils/Images";

export default function GuestNavbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", href: "/", hasDropdown: false },
    { 
      name: "About Us", 
      href: "/about", 
      hasDropdown: false,
      
    },
    { 
      name: "Help Center", 
      href: "/help", 
      hasDropdown: false,
      
    },
    { 
      name: "Support", 
      href: "/support", 
      hasDropdown: false,
    },
    { 
      name: "Blog", 
      href: "/blog", 
      hasDropdown: false,
    }
  ];

  const handleDropdownToggle = (index) => {
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "border-b shadow-2xl backdrop-blur-xl bg-white/95 border-gray-200/50"
          : "backdrop-blur-lg bg-gray-900/90"
      }`}
    >
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center space-x-3"
          >
            <img 
              src={isScrolled ? Images.logo_colored : Images.logo_white}
              alt="TripSecure Logo" 
              className="h-8 w-auto transition-all duration-300"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex lg:items-center lg:space-x-1">
            {navItems.map((item, index) => (
              <div key={item.name} className="relative">
                {item.hasDropdown ? (
                  <div className="relative">
                    <motion.button
                      whileHover={{ y: -2 }}
                      onClick={() => handleDropdownToggle(index)}
                      className={`flex items-center px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-300 hover:bg-gray-800/50 ${
                        isScrolled 
                          ? "text-gray-700 hover:text-blue-600 hover:bg-gray-100/50" 
                          : "text-gray-300 hover:text-white hover:bg-gray-800/50"
                      }`}
                    >
                      {item.name}
                      <FaChevronDown 
                        size={12} 
                        className={`ml-2 transition-transform duration-300 ${
                          activeDropdown === index ? 'rotate-180' : ''
                        }`}
                      />
                    </motion.button>
                    
                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {activeDropdown === index && (
                        <motion.div
                          initial={{ opacity: 0, y: 10, scale: 0.95 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 10, scale: 0.95 }}
                          transition={{ duration: 0.2 }}
                          className={`absolute left-0 top-full mt-2 w-64 rounded-xl border shadow-2xl backdrop-blur-xl overflow-hidden ${
                            isScrolled 
                              ? "bg-white/95 border-gray-200/50" 
                              : "bg-gray-800/95 border-gray-700/50"
                          }`}
                        >
                          <div className="py-2">
                            {item.dropdownItems.map((dropdownItem) => (
                              <Link
                                key={dropdownItem.name}
                                to={dropdownItem.href}
                                className={`block px-4 py-3 transition-all duration-200 ${
                                  isScrolled 
                                    ? "text-gray-700 hover:text-blue-600 hover:bg-gray-100/50" 
                                    : "text-gray-300 hover:text-white hover:bg-gray-700/50"
                                }`}
                                onClick={() => setActiveDropdown(null)}
                              >
                                {dropdownItem.name}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div whileHover={{ y: -2 }}>
                    <Link
                      to={item.href}
                      className={`block px-3 py-1.5 text-sm font-medium rounded-lg transition-all duration-300 hover:bg-gray-800/50 ${
                        isScrolled 
                          ? "text-gray-700 hover:text-blue-600 hover:bg-gray-100/50" 
                          : "text-gray-300 hover:text-white hover:bg-gray-800/50"
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex lg:items-center lg:space-x-3">
            <motion.button
              onClick={() => navigate("/signin")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-300 ${
                isScrolled
                  ? "text-gray-700 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                  : "text-white border-2 border-white/30 hover:border-white/50 hover:bg-white/10"
              }`}
            >
              Sign In
            </motion.button>
            <motion.button
              onClick={() => navigate("/signup")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-5 py-1.5 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full shadow-lg transition-all duration-300 hover:from-blue-600 hover:to-indigo-700 hover:shadow-xl hover:shadow-blue-500/25"
            >
              Get Started
            </motion.button>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center lg:hidden">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-lg transition-colors duration-200 ${
                isScrolled 
                  ? "text-gray-700 hover:text-blue-600 hover:bg-gray-100/50" 
                  : "text-gray-300 hover:text-white hover:bg-gray-800/50"
              }`}
            >
              {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className={`border-t backdrop-blur-xl lg:hidden ${
              isScrolled 
                ? "bg-white/95 border-gray-200/50" 
                : "bg-gray-800/95 border-gray-700/50"
            }`}
          >
            <div className="px-4 py-6 space-y-4">
              {navItems.map((item) => (
                <div key={item.name}>
                  <Link
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={`block py-3 font-medium border-b transition-colors duration-200 ${
                      isScrolled 
                        ? "text-gray-700 hover:text-blue-600 border-gray-200/50" 
                        : "text-gray-300 hover:text-white border-gray-700/50"
                    }`}
                  >
                    {item.name}
                  </Link>
                  {item.hasDropdown && (
                    <div className="mt-2 ml-4 space-y-2">
                      {item.dropdownItems.map((dropdownItem) => (
                        <Link
                          key={dropdownItem.name}
                          to={dropdownItem.href}
                          onClick={() => setIsOpen(false)}
                          className={`block py-2 text-sm transition-colors duration-200 ${
                            isScrolled 
                              ? "text-gray-600 hover:text-blue-600" 
                              : "text-gray-400 hover:text-white"
                          }`}
                        >
                          {dropdownItem.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              <div className={`pt-4 space-y-3 border-t ${
                isScrolled ? "border-gray-200/50" : "border-gray-700/50"
              }`}>
                <button 
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/signin");
                  }} 
                  className={`px-6 py-3 w-full font-medium rounded-full transition-all duration-300 ${
                    isScrolled
                      ? "text-gray-700 border-2 border-gray-300 hover:border-gray-400 hover:bg-gray-50"
                      : "text-white border-2 border-white/30 hover:border-white/50 hover:bg-white/10"
                  }`}
                >
                  Sign In
                </button>
                <button 
                  onClick={() => {
                    setIsOpen(false);
                    navigate("/signup");
                  }} 
                  className="px-6 py-3 w-full font-semibold text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-300 hover:from-blue-600 hover:to-indigo-700"
                >
                  Get Started
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop for dropdowns */}
      {activeDropdown !== null && (
        <div 
          className="fixed inset-0 z-40 lg:hidden" 
          onClick={() => setActiveDropdown(null)}
        />
      )}
    </motion.nav>
  );
}
